<script>
  import get from "lodash/get";
  import range from "lodash/range";
  import { createEventDispatcher, getContext, onMount } from "svelte";
  import { Modal } from "svelte-utilities";
  import { bucketArray } from "overline";

  import TrashIcon from "@local/assets/icons/trash.svg";
  import BlankIcon from "@local/assets/icons/blank.svg";
  import SortIcon from "@local/assets/icons/sort.svg";

  import SidebarSection from "./SidebarSection.svelte";
  import NewLocationButtons from "./NewLocationButtons.svelte";
  import Viewport from "#lib/drawing/ViewportFixed.svelte";

  import {
    docLocationTab as tab,
    documentViewShowItems,
    documentViewShowCollections,
    documentViewShowTypes,
    documentViewShowNotes,
    documentViewItemFilter,
    documentViewCollectionFilter,
    documentViewTypeFilter,
    documentViewNoteFilter,
    selectedRecords,
  } from "#src/stores/ui.js";
  import { discontiguousKey } from "#src/extensions/event-keys.js";
  import liteDrawingMini from "@local/extensions/drawing/lite-drawing-mini.js";
  import formatDims from "@local/extensions/formatters/format-dims.js";
  import locationsByDocument from "#src/extensions/locations-by-document.js";
  import { filterItems } from "#src/extensions/filter-items.js";

  import { sortByDirection, sortBy, sortCollectionsBy, sortCollectionsByDirection } from "#src/stores/ui.js";
  import { UX_MODE } from "#src/env.js";

  export let group;
  export let document;
  export let items;
  export let types;
  export let documentNotes;
  export let collections;
  export let adding;
  export let hoveredLocations;
  export let customColumns;
  export let customColColumns;
  export let standardColumns;
  export let disabled;

  const dispatch = createEventDispatcher();
  const typeColors = getContext("typeColors");

  let sortTypesDirection = "asc";
  let sortNotesDirection = "asc";

  let lastSelectedRecord = null;
  let deleteRecordsModal;
  let recordsToDelete = [];
  let recordsWithLocations = [];

  $: docLocations = locationsByDocument($group);
  $: locations = docLocations[document.id] || [];
  $: updateTab(
    adding,
    $documentViewShowItems,
    $documentViewShowCollections,
    $documentViewShowTypes,
    $documentViewShowNotes,
  );
  $: records = selectRecords(
    $tab,
    items,
    types,
    group,
    $documentViewItemFilter,
    $documentViewCollectionFilter,
    $documentViewTypeFilter,
    $documentViewNoteFilter,
  );
  $: selectedRecordsList = records.filter((r) => $selectedRecords[r.id]);
  $: allLocationsByRecord = bucketArray(
    $group.locations.order
      .map((id) => $group.locations[id])
      .filter((l) => {
        return $group.documents[l.document_id]?.data?.pages?.[l.page_id];
      }),
    "record_id",
  );
  $: locationsByRecord = bucketArray(locations, "record_id");

  const tabNames = {
    item: "items",
    collection: UX_MODE === "shpx" ? "frames" : "openings",
    type: "types",
    document_note: "notes",
  };

  function updateTab(adding, showItems, showCollections, showTypes, showNotes) {
    if (adding?.record_type && adding.record_type !== $tab) {
      $tab = adding.record_type;
    } else {
      if (!showItems && $tab === "item") {
        $tab = "collection";
      }

      if (!showCollections && $tab === "collection") {
        $tab = "type";
      }

      if (!showTypes && $tab === "type") {
        $tab = "document_note";
      }

      if (!showNotes && $tab === "document_note") {
        $tab = "item";
      }
    }
  }

  function selectRecords(tab, items, types, group, itemFilter, collectionFilter, typeFilter, noteFilter) {
    if (tab === "item") {
      if (!itemFilter) return items;
      return filterItems(items, itemFilter);
    }
    if (tab === "type") {
      if (!typeFilter) return types;
      return types;
    }
    if (tab === "collection") {
      if (!collectionFilter) return collections;
      return filterItems(collections, collectionFilter);
    }
    if (tab === "document_note") {
      if (!noteFilter) return documentNotes;
      return documentNotes;
    }
    return [];
  }

  function description(item) {
    const mark = item.mark || "";
    const labelBy = item.is_collection
      ? $group.data.settings.collections_label_by
      : $group.data.settings.label_by;
    const label = (labelBy && get(item, labelBy)) || "";

    let textLbl = label && label !== mark ? `${mark} - ${label}` : mark;

    if (item.width && item.height) {
      return `${textLbl} - ${formatDims(item, $group.data.settings)}`;
    } else {
      return textLbl;
    }
  }

  function sortRecords() {
    if ($tab === "item") {
      $sortBy = "mark";
      $sortByDirection = $sortBy === "mark" && $sortByDirection === "asc" ? "desc" : "asc";
      group.sortItems($sortBy, $sortByDirection, { prop: "is_collection", value: true });
    } else if ($tab === "collection") {
      $sortCollectionsBy = "mark";
      $sortCollectionsByDirection =
        $sortCollectionsBy === "mark" && $sortCollectionsByDirection === "asc" ? "desc" : "asc";
      group.sortItems($sortCollectionsBy, $sortCollectionsByDirection, {
        prop: "is_collection",
        value: false,
      });
    } else if ($tab === "type") {
      const order = $group.types.order.toSorted((aid, bid) => {
        const amark = $group.types[aid].mark || "";
        const bmark = $group.types[bid].mark || "";
        return amark.localeCompare(bmark);
      });

      if (sortTypesDirection === "desc") {
        order.reverse();
      }

      sortTypesDirection = sortTypesDirection === "asc" ? "desc" : "asc";
      group.updateTypeOrder(order);
    } else if ($tab === "document_note") {
      const order = $group.document_notes.order.toSorted((aid, bid) => {
        const amark = $group.document_notes[aid].mark || "";
        const bmark = $group.document_notes[bid].mark || "";
        return amark.localeCompare(bmark);
      });

      if (sortNotesDirection === "desc") {
        order.reverse();
      }

      sortNotesDirection = sortNotesDirection === "asc" ? "desc" : "asc";
      group.updateDocumentNoteOrder(order);
    }
  }

  function recordType(id) {
    if ($group.items[id]) {
      const item = $group.items[id];
      if (item.is_collection) {
        return "collection";
      }
      return "item";
    }

    if ($group.types[id]) return "type";
    if ($group.document_notes[id]) return "document_note";
    return null;
  }

  function clickRecord(e, record) {
    if (e.shiftKey) {
      let newSelected;
      const rtype = recordType(lastSelectedRecord);
      if (rtype && rtype !== $tab) {
        $selectedRecords = {};
        lastSelectedRecord = null;
      }
      const prev = lastSelectedRecord || records[0]?.id;
      const prevIndex = records.findIndex((r) => r.id === prev);
      const currentIndex = records.findIndex((r) => r.id === record.id);
      const min = Math.min(prevIndex, currentIndex);
      const max = Math.max(prevIndex, currentIndex);
      newSelected = range(min, max + 1).map((i) => records[i].id);
      selectedRecords.select(...newSelected);
      lastSelectedRecord = record.id;
    } else if (discontiguousKey(e)) {
      selectedRecords.toggle(record.id);
      lastSelectedRecord = record.id;
    } else {
      selectedRecords.selectOnly(record.id);
      lastSelectedRecord = record.id;
    }
  }

  function unhover(id) {
    hoveredLocations = {};
  }

  function hover(id) {
    const locations = locationsByRecord[id];
    if (locations) {
      hoveredLocations = locations.reduce((acc, loc) => {
        acc[loc.id] = true;
        return acc;
      }, {});
    }
  }

  function confirmDeleteRecords() {
    recordsToDelete = [...selectedRecordsList];
    recordsWithLocations = recordsToDelete.filter((r) => (allLocationsByRecord[r.id] || []).length > 0);
    deleteRecordsModal.open();
  }

  function deleteRecords() {
    const locations = recordsWithLocations.reduce((acc, r) => {
      const locs = allLocationsByRecord[r.id] || [];
      acc.push(...locs.map((l) => l.id));
      return acc;
    }, []);
    const records = recordsToDelete.map((r) => r.id);
    let recordType;
    if ($tab === "item" || $tab === "collection") {
      recordType = "item";
    } else {
      recordType = $tab;
    }

    const updates = [
      { type: "location", action: "remove", ids: locations, block: true },
      { type: recordType, action: "remove", ids: records },
    ];
    group.update(updates);
    $selectedRecords = {};
  }

  onMount(() => {
    if (UX_MODE === "shpx") {
      $tab = "collection";
    }
  });
</script>

{#if $documentViewShowItems || $documentViewShowCollections || $documentViewShowTypes || $documentViewShowNotes}
  <div class="grow overflow-hidden flex flex-col" style="min-height: 12rem;">
    <SidebarSection>
      <h3 class="mb-2 px-2">Markers</h3>
      <div class="px-2 flex items-center justify-between">
        <div class="flex items-center gap-2">
          {#if UX_MODE !== "shpx"}
            {#if $documentViewShowItems}
              <button class="location-tab" class:current={$tab === "item"} on:click={() => ($tab = "item")}
                >Items</button>
            {/if}
          {/if}
          {#if $documentViewShowCollections}
            <button
              class="location-tab"
              class:current={$tab === "collection"}
              on:click={() => ($tab = "collection")}>{UX_MODE === "shpx" ? "Frames" : "Openings"}</button>
          {/if}
          {#if UX_MODE !== "shpx"}
            {#if $documentViewShowTypes}
              <button class="location-tab" class:current={$tab === "type"} on:click={() => ($tab = "type")}
                >Types</button>
            {/if}
            {#if $documentViewShowNotes}
              <button
                class="location-tab"
                class:current={$tab === "document_note"}
                on:click={() => ($tab = "document_note")}>
                Notes
              </button>
            {/if}
          {/if}
        </div>
        <div class="text-gray-500 flex gap-1">
          <button class="rounded hover:bg-gray-200" on:click={sortRecords}>
            <SortIcon />
          </button>
          <div>#</div>
        </div>
      </div>
    </SidebarSection>

    <div class="overflow-y-auto doc-location-container">
      <SidebarSection>
        <div class="border-t">
          {#each records as record (record.id)}
            {@const current = adding?.record_id === record?.id}
            {@const locQty = locationsByRecord[record.id]?.length || 0}
            {@const allLocQty = allLocationsByRecord[record.id]?.length || 0}
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <div
              class="flex w-full justify-between px-2 gap-2 select-none items-center doc-location-record"
              class:bg-amber-100={current && !$selectedRecords[record.id]}
              class:bg-blue-500={$selectedRecords[record.id]}
              class:text-white={$selectedRecords[record.id]}
              on:mouseout={() => unhover(record.id)}
              on:mouseover={() => hover(record.id)}
              on:focus={() => hover(record.id)}
              on:blur={() => unhover(record.id)}>
              {#if $tab === "type"}
                <div class="flex gap-2 grow overflow-hidden">
                  {#if !disabled}
                    <NewLocationButtons
                      record_type={$tab}
                      record_id={record.id}
                      on:add-location
                      on:clone-location />
                  {/if}
                  <button
                    class="truncate grow text-left outline-none py-1"
                    on:click={(e) => clickRecord(e, record)}
                    on:dblclick={() =>
                      dispatch("dblclick-locations", { locations: locationsByRecord[record.id] || [] })}
                    >{record.mark} - {record.name}</button>
                </div>
              {:else if $tab === "document_note"}
                <div class="flex gap-2 grow overflow-hidden">
                  {#if !disabled}
                    <NewLocationButtons
                      record_type={$tab}
                      record_id={record.id}
                      on:add-location
                      on:clone-location />
                  {/if}
                  <button
                    class="truncate grow text-left outline-none py-1"
                    on:click={(e) => clickRecord(e, record)}
                    on:dblclick={() =>
                      dispatch("dblclick-locations", { locations: locationsByRecord[record.id] || [] })}
                    >{record.mark} - {record.title}</button>
                  {#if allLocQty <= 0 && !disabled}
                    <button
                      class="flex-none"
                      on:click={() => dispatch("delete-record", { record_type: $tab, record_id: record.id })}>
                      <TrashIcon />
                    </button>
                  {/if}
                </div>
              {:else}
                <div class="flex gap-2 grow overflow-hidden">
                  {#if !disabled}
                    <NewLocationButtons
                      record_type={$tab}
                      record_id={record.id}
                      on:add-location
                      on:clone-location />
                  {/if}
                  <button
                    class="truncate grow text-left flex gap-2 outline-none py-1"
                    on:click={(e) => clickRecord(e, record)}
                    on:dblclick={() =>
                      dispatch("dblclick-locations", { locations: locationsByRecord[record.id] || [] })}>
                    <div class="flex gap-2">
                      <div class="w-4 h-4 relative">
                        <Viewport
                          padding={1}
                          drawing={liteDrawingMini(record, { typeColor: $typeColors[record.type_id] })}
                          width={16}
                          height={16} />
                      </div>
                      <div class="truncate">
                        {description(record)}
                      </div>
                    </div>
                  </button>
                </div>
              {/if}
              <div
                class:text-white={$selectedRecords[record.id]}
                class:text-gray-500={!$selectedRecords[record.id] && locQty <= record.quantity}
                class:text-black={locQty >= record.quantity}
                class:font-bold={locQty > record.quantity}
                class:italic={locQty > record.quantity}
                class="whitespace-nowrap doc-location-count">
                {locationsByRecord[record.id]?.length || 0}
                {#if $tab === "item"}
                  / {record.quantity}
                {:else if $tab === "document_note" && allLocQty !== locQty}
                  ({allLocQty})
                {/if}
              </div>
            </div>
          {/each}
        </div>
      </SidebarSection>
    </div>

    <SidebarSection>
      <div
        class="flex justify-between pt-1 pb-3 pr-2 border border-transparent rounded"
        class:bg-amber-100={adding && !adding.record_id}
        class:border-amber-500={adding && !adding.record_id}>
        <div class="px-1">
          {#if selectedRecordsList.length && !disabled}
            <button class="p-1 rounded hover:bg-gray-200" on:click={confirmDeleteRecords}>
              <TrashIcon />
            </button>
          {:else}
            <div class="p-1">
              <BlankIcon />
            </div>
          {/if}
        </div>
        <button
          class="font-bold"
          on:click={() => dispatch("add-location", { record_type: $tab, record_id: null })}>
          {#if $tab === "type"}
            + New type
          {:else if $tab === "collection"}
            + New {UX_MODE === "shpx" ? "frame" : "opening"}
          {:else if $tab === "item"}
            + New item
          {:else if $tab === "document_note"}
            + New note
          {/if}
        </button>
      </div>
    </SidebarSection>
    <button class="w-full grow cursor-default" on:click={() => ($selectedRecords = {})}> </button>
  </div>
{/if}

<Modal
  bind:this={deleteRecordsModal}
  on:confirm={deleteRecords}
  closeable
  buttons={[
    {
      label: "Cancel",
      type: "cancel",
    },
    {
      label: "Delete",
      type: "confirm",
      style: "danger",
    },
  ]}>
  <div slot="title">Confirm Delete</div>
  <div slot="content" class="text-sm space-y-2">
    <p>You have selected {recordsToDelete.length} {tabNames[$tab]} for deletion.</p>
    {#if recordsWithLocations.length}
      <p>
        {recordsWithLocations.length} of these {tabNames[$tab]} have markers that will also be deleted.
      </p>
      <p>Are you sure you want to delete these {tabNames[$tab]} and their markers?</p>
    {:else}
      <p>Are you sure you want to delete these {tabNames[$tab]}?</p>
    {/if}
  </div>
</Modal>

<style lang="scss">
  .location-tab {
    @apply border-b py-1 text-gray-500;
    border-color: transparent;

    &.current {
      @apply text-black border-black;
    }
  }
</style>
